import { GlobalConfig } from "config";
import { io, Socket } from "socket.io-client";
import { UserTracker } from "user-tracker";
const ENV = {
  SOCKET_SERVER: GlobalConfig.SOCKET_SERVER,
};

export class SocketConnector {
  static uuid = new Date().getTime().toString(); // To Identify each browser session
  static socket: Socket<any, any>;
  static connectionLogs: {
    message: string;
    time: string;
    isOnline: boolean;
  }[] = [];
  static lastToken: string;
  static lastDisconnectTime: number = 0;
  private static hasAttachedCoreListeners = false;
  private static hasAttachedVisibilityListener = false;
  private static lastActivityTime: number = Date.now();
  private static watchdogInterval: any = null;

  static initWatchdog() {
    if (typeof window === "undefined") return;

    window.addEventListener("online", () => {
      console.log("SC: Network online event detected");
      const socket = SocketConnector.socket;
      if (socket && !socket.connected) {
        socket.connect();
      }
    });

    if (this.watchdogInterval) return;
    this.watchdogInterval = setInterval(() => {
      const socket = SocketConnector.socket;
      if (!socket) return;

      const idleTime = Date.now() - SocketConnector.lastActivityTime;
      // If connected but no packet received for 45s, connection is likely half-open (zombie). Force reconnect!
      if (socket.connected && idleTime > 45000) {
        console.warn(
          "SC: Watchdog detected silent idle connection (>45s). Reconnecting...",
        );
        socket.disconnect();
        socket.connect();
        SocketConnector.lastActivityTime = Date.now();
      } else if (
        !socket.connected &&
        (socket.io as any)?.readyState !== "opening"
      ) {
        console.log(
          "SC: Watchdog detected disconnected socket. Reconnecting...",
        );
        socket.connect();
      }
    }, 15000);
  }

  static initVisibilityAutoWake() {
    if (typeof document === "undefined" || this.hasAttachedVisibilityListener)
      return;
    this.hasAttachedVisibilityListener = true;

    const checkAndWake = () => {
      if (document.visibilityState === "visible") {
        const socket = SocketConnector.socket;
        if (!socket) return;
        if (!socket.connected && (socket.io as any)?.readyState !== "opening") {
          console.log("SC: Tab focused/visible, reconnecting socket");
          socket.connect();
        }
      }
    };

    document.addEventListener("visibilitychange", checkAndWake);
    window.addEventListener("focus", checkAndWake);
  }

  static connect(auth: string) {
    // The following condition is added to support HMR
    if (!auth) return;

    this.initVisibilityAutoWake();
    this.initWatchdog();

    const existingSocket = SocketConnector.socket;

    // If token is same, don't recreate the socket while it's still "opening".
    if (existingSocket && this.lastToken === auth) {
      if (existingSocket.connected) return;
      // Socket.io v4 manager readyState: "opening" | "open" | "closed"
      if ((existingSocket.io as any)?.readyState === "opening") return;

      // If it got disconnected (or never connected), ensure it reconnects.
      existingSocket.connect();
      return;
    }

    // Token changed or first time: tear down and create fresh socket.
    this.lastToken = auth;
    this.hasAttachedCoreListeners = false;

    if (existingSocket) {
      existingSocket.removeAllListeners();
      existingSocket.disconnect();
      (existingSocket.io as any)?.removeAllListeners?.();
    }

    const socket = io(ENV.SOCKET_SERVER, {
      query: {
        namespace: "user",
        accessToken: auth,
        uuid: this.uuid,
      },
      secure: window.location.protocol === "https:",
      autoConnect: true,
      // Don't hard-force websocket only; allow polling fallback for flaky first loads.
      transports: ["websocket", "polling"],
      upgrade: true,
      rememberUpgrade: true,
      reconnectionDelayMax: 2000,
    });

    SocketConnector.socket = socket;
    this.patchSocketListener();
    this.onConnected();
  }

  static disconnect() {
    if (SocketConnector.socket) {
      SocketConnector.socket.disconnect();
    }
  }

  private static patchSocketListener() {
    // Legacy wildcard support (`socket.on("*")`) used in `handle-socket-events.ts`.
    // Socket.IO v4 supports `onAny`, but this project relies on `on("*")`.
    const anySocket = this.socket as any;
    const onevent = anySocket?.onevent;
    if (typeof onevent !== "function") return;

    if (anySocket.__cc_patched_onevent) return;
    anySocket.__cc_patched_onevent = true;

    anySocket.onevent = function (packet) {
      SocketConnector.lastActivityTime = Date.now();
      const args = packet.data || [];
      onevent.call(this, packet); // original call
      packet.data = ["*"].concat(args);
      onevent.call(this, packet); // additional call to catch-all
    };
  }

  private static onConnected() {
    const socket = SocketConnector.socket;

    if (this.hasAttachedCoreListeners) return;
    this.hasAttachedCoreListeners = true;

    socket.on("connect", () => {
      SocketConnector.lastActivityTime = Date.now();
      // performance.navigation.type == performance.navigation.TYPE_RELOAD
      console.log("SC: Connected");
      this.connectionLogs.push({
        message: "Connected",
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });

      UserTracker.track("Socket Connected");
    });

    socket.io.on("error", (err) => {
      console.log("SC: Manager error", err);
      this.connectionLogs.push({
        message: `Manager error: ${String((err as any)?.message || err)}`,
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });
    });

    socket.on("connect_error", (err) => {
      console.log("SC: Connect error", err);
      this.connectionLogs.push({
        message: `Connect error: ${String((err as any)?.message || err)}`,
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });
    });

    socket.on("reconnect_error", (err) => {
      console.log("SC: Reconnect error", err);
      this.connectionLogs.push({
        message: `Reconnect error: ${String((err as any)?.message || err)}`,
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });
    });

    socket.on("reconnect_failed", () => {
      console.log("SC: Reconnect failed");
      this.connectionLogs.push({
        message: "Reconnect failed",
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });
    });

    socket.on("reconnect", () => {
      SocketConnector.lastActivityTime = Date.now();
      console.log("SC: Reconnected");
      this.connectionLogs.push({
        message: "Reconnected",
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });
      // If it greater than 5 minutes, reload the page
      if (
        this.lastDisconnectTime &&
        this.lastDisconnectTime + 5 * 60 * 1000 < new Date().getTime()
      ) {
        window.location.reload();
        return;
      }
      UserTracker.track("Socket Re-Connected");
    });

    socket.on("disconnect", (reason) => {
      // Todo: Not sure at what time disconnected gets logged when pc goes to sleep
      // Probably need to have a timer that logs the last active time do the location reload logic above
      console.log("SC: Disconnected", reason);
      this.connectionLogs.push({
        message: "Disconnected",
        time: new Date().toString(),
        isOnline: window.navigator.onLine,
      });

      this.lastDisconnectTime = new Date().getTime();
      UserTracker.track("Socket Disconnected");
    });

    socket.on("KNOCK_KNOCK", () => {
      console.log("KNOCK_KNOCK");
      socket.emit("YES_YES");
    });

    socket.on("RELOAD", () => {
      console.log("RELOAD");
      window.location.reload();
    });
  }

  static req(event: string, data) {
    // Todo: Got to re-check this.
    // The idea is to make a request to socket server and return the response through await interface
    return new Promise((resolve, reject) => {
      const maxTimeout = 5000;
      const timer = setTimeout(() => {
        throw new Error("Max Timeout Exceeded");
      }, maxTimeout);
      this.socket.emit(event, data, (response: any) => {
        clearTimeout(timer);
        resolve(response);
      });
    });
  }
}
